import React, { useEffect, useState } from "react";
import { api } from "@/api/client.js";
import { torontoClock, localStampClock } from "@/lib/huntDates.js";
import { weatherFace, windFr } from "@/lib/weatherFace.js";

const WEATHER_KEY = "orignal2026.weather";
const DAYS = [
  ["2026-10-02", "Ven 2"],
  ["2026-10-03", "Sam 3"],
  ["2026-10-04", "Dim 4"],
  ["2026-10-05", "Lun 5"],
  ["2026-10-06", "Mar 6"],
  ["2026-10-07", "Mer 7"],
  ["2026-10-08", "Jeu 8"],
  ["2026-10-09", "Ven 9"],
];
const BLOCKS = [
  ["morning", "Matin", false],
  ["noon", "Midi", false],
  ["afternoon", "PM", false],
  ["night", "Nuit", true],
];

function whole(value) {
  const number = Number(value);
  return Number.isFinite(number) ? String(Math.round(number)) : "—";
}

function degree(value) {
  const text = whole(value);
  return text === "—" ? "—" : `${text}°`;
}

function compass(dir) {
  const named = windFr(dir);
  if (named && named !== dir) return named;
  const degrees = Number(dir);
  if (!Number.isFinite(degrees)) return "—";
  const labels = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
  return labels[Math.round((((degrees % 360) + 360) % 360) / 45) % 8];
}

function clock(stamp) {
  return localStampClock(stamp);
}

function moonClock(stamp) {
  if (!stamp) return "Pas ce jour-là";
  return clock(stamp);
}

function trendText(trend) {
  if (!trend || !Number.isFinite(Number(trend.delta))) return "À confirmer";
  const delta = Math.round(Number(trend.delta));
  if (Math.abs(delta) <= 1) return "stable d’un matin à l’autre";
  if (delta > 0) return `plus doux demain matin, +${delta}°`;
  return `plus froid demain matin, ${delta}°`;
}

function dayPack(body, iso) {
  return (body?.days || []).find((row) => row.date === iso) || null;
}

export function HuntConditions({ day }) {
  const initial = DAYS.some(([iso]) => iso === day) ? day : "2026-10-02";
  const [selected, setSelected] = useState(initial);
  const [data, setData] = useState(null);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    let stop = false;
    api("/api/weather").then((body) => {
      if (stop) return;
      if (body?.ok) {
        window.localStorage.setItem(WEATHER_KEY, JSON.stringify(body));
        setData(body);
        return;
      }
      const cached = window.localStorage.getItem(WEATHER_KEY);
      setData(cached ? { ...JSON.parse(cached), stale: true, offline: !navigator.onLine } : { ok: false });
    }).catch(() => {
      if (stop) return;
      const cached = window.localStorage.getItem(WEATHER_KEY);
      setData(cached ? { ...JSON.parse(cached), stale: true, offline: true } : { ok: false, offline: !navigator.onLine });
    });
    return () => { stop = true; };
  }, []);

  if (!data) return <p className="og-hint" data-testid="hunt-conditions">Mise à jour des conditions…</p>;
  if (!data.ok) {
    return (
      <section data-testid="hunt-conditions">
        <p>Conditions indisponibles pour le moment.</p>
        <p data-testid="weather-fresh">Dernière mise à jour : inconnue</p>
      </section>
    );
  }

  const pack = dayPack(data, selected) || {};
  const moments = pack.moments || data.moments || {};
  const live = data.today === selected;
  const now = live ? (data.current || {}) : (moments.noon || moments.morning || {});
  const face = weatherFace(live ? data.current?.weather_code : now.weather_code, false);
  const hours = (pack.hours || data.hours || data.hourly || []).filter((hour) => hour?.hour);
  const shown = hours.filter((hour) => ["05", "07", "09", "11", "13", "15", "17", "19", "21"].includes(String(hour.hour).slice(0, 2)));
  const nextIso = DAYS[DAYS.findIndex(([iso]) => iso === selected) + 1]?.[0];
  const next = nextIso ? dayPack(data, nextIso) : null;
  const moon = pack.moon || data.moon || {};
  const fresh = data.fetched_at ? torontoClock(data.fetched_at) : "—";
  const offline = !online || data.offline || data.stale;
  const shifts = pack.wind_shifts || data.wind_shifts || [];

  return (
    <section className="og-cond" data-testid="hunt-conditions">
      <header className="og-visual-hero og-visual-hero--moose">
        <p className="og-kicker">Secteur Desrochers</p>
      </header>
      <p>Secteur Desrochers</p>
      <div className="og-cond-days" data-testid="cond-days">
        {DAYS.map(([iso, label]) => (
          <button key={iso} type="button" className={`og-btn${selected === iso ? " is-on" : ""}`} data-testid={`cond-day-${iso}`} onClick={() => setSelected(iso)}>{label}</button>
        ))}
      </div>
      <p data-testid="weather-fresh">Dernière mise à jour : {fresh}</p>
      {offline ? <p data-testid="weather-stale">Hors ligne — dernière donnée connue</p> : null}

      <article className="og-sheetline" data-testid="cond-now">
        <h3>{live ? "En ce moment" : "État prévu"}</h3>
        <p>{face.icon} {face.label || "Condition non reçue"} · {degree(live ? data.current?.temperature : now.temperature)}</p>
        <p>Ressenti {degree(live ? data.current?.feels : now.feels)}</p>
        <p>Min {degree(pack.min ?? data.daily?.min)} · Max {degree(pack.max ?? data.daily?.max)}</p>
        <p data-testid="cond-trend">Tendance 24 h : {trendText(pack.trend_24h || data.trend_24h)}</p>
      </article>

      <div className="og-moments" data-testid="weather-moments">
        {BLOCKS.map(([key, label, night]) => {
          const row = moments[key] || {};
          const look = weatherFace(row.weather_code, night || row.night);
          return (
            <article key={key} className={`og-mood og-mood--${key}`} data-testid={`cond-${key}`}>
              <h4>{label}</h4>
              <p>{look.icon} {degree(row.temperature)} — {look.label}</p>
              <p>Vent {compass(row.wind_dir)} {whole(row.wind)} km/h</p>
              {row.gust != null ? <p>Rafales {whole(row.gust)} km/h</p> : null}
              <p>Pluie {whole(row.rain)} %</p>
            </article>
          );
        })}
      </div>

      <article className="og-sheetline" data-testid="cond-wind">
        <h3>Vent</h3>
        <p>{compass(now.wind_dir)} {whole(live ? data.current?.wind_speed : now.wind)} km/h · rafales {whole(live ? data.current?.gust : (now.gust ?? pack.gust_max))} km/h</p>
        <p>{shifts.length > 1 ? shifts.map((row) => `${row.label} ${compass(row.wind_dir)}`).join(" · ") : "Direction stable sur la journée"}</p>
      </article>

      <article className="og-sheetline" data-testid="cond-rain">
        <h3>Précipitations</h3>
        <p>{whole(pack.rain ?? data.daily?.rain)} % · {whole(pack.precip_mm ?? data.precip_mm)} mm prévus</p>
        <p>Fenêtre de pluie : {pack.rain_window || data.rain_window || "À confirmer"}</p>
        <p>Nuages {whole(live ? data.current?.cloud : now.cloud)} % · humidité {whole(live ? data.current?.humidity : now.humidity)} % · point de rosée {degree(live ? data.current?.dew : now.dew)}</p>
      </article>

      <article className="og-sheetline" data-testid="cond-pressure">
        <h3>Pression</h3>
        <p>{whole(pack.pressure ?? data.current?.pressure)} hPa · {pack.pressure_trend || "à confirmer"}</p>
        <p data-testid="cond-pressure-note">La pression est une condition à suivre. Elle ne garantit pas l’activité de l’orignal.</p>
      </article>

      <article className="og-sheetline" data-testid="cond-sun">
        <h3>Lumière</h3>
        <p>Aube {clock(pack.dawn || data.dawn)} · Lever {clock(pack.sunrise || data.sunrise)}</p>
        <p>Coucher {clock(pack.sunset || data.sunset)} · Crépuscule {clock(pack.dusk || data.dusk)}</p>
        <p data-testid="cond-moose">La température, l’aube et le crépuscule sont des repères utiles pour le déplacement de l’orignal. Ce n’est pas une prédiction.</p>
      </article>

      <article className="og-sheetline" data-testid="cond-moon">
        <h3>Lune</h3>
        <p>{moon.name || "À confirmer"} · illumination {moon.illumination == null ? "À confirmer" : `${whole(moon.illumination)} %`}</p>
        <p>Lever {moonClock(moon.rise)} · Coucher {moonClock(moon.set)}</p>
        <p data-testid="cond-moon-note">La lune est une condition à suivre. Elle ne garantit pas l’activité.</p>
      </article>

      {next ? (
        <article className="og-sheetline" data-testid="cond-compare">
          <h3>Demain / aujourd’hui</h3>
          <p>Max {degree(pack.max)} → {degree(next.max)} · Min {degree(pack.min)} → {degree(next.min)}</p>
          <p>Pluie {whole(pack.rain)} % → {whole(next.rain)} % · {whole(pack.precip_mm)} mm → {whole(next.precip_mm)} mm</p>
        </article>
      ) : <p data-testid="cond-compare">Pas de journée suivante dans le séjour.</p>}

      <details className="og-hours-fold" data-testid="weather-hours-fold">
        <summary>Voir le détail heure par heure</summary>
        <table className="og-hours" data-testid="weather-hours">
          <thead>
            <tr><th>Heure</th><th>Température</th><th>Vent</th><th>Pluie</th></tr>
          </thead>
          <tbody>
            {shown.map((hour) => (
              <tr key={hour.hour}>
                <td>{Number(String(hour.hour).slice(0, 2))} h</td>
                <td>{degree(hour.temperature)}</td>
                <td>{compass(hour.wind_dir)} {whole(hour.wind)} km/h</td>
                <td>{whole(hour.rain)} %</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </section>
  );
}
